import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { Badge, Button, Spinner } from 'react-bootstrap';
import { RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { fetchChartRoles, confirmChartRole } from '../../../services/api/chartRolesApi';
import { getApiUrl as getVatApiUrl } from '../../../utils/ApiUtils';
import { brandSelectTheme } from '../Common/reactSelectTheme';

/**
 * Kontoplansroller — the tenant's semantic posting defaults.
 *
 * Every booking path resolves accounts through these roles (the backend's
 * ChartRoleService); this surface shows each role's resolution status and is
 * the SANCTIONED way to re-point a default at another account — including a
 * legacy/custom occupant preserved by an in-place chart migration (spec §3.4
 * R1: the role surfaces as "behöver mappning" until the user confirms it).
 *
 * The chart version shown is the server's own resolution; nothing here lets
 * the client pick which catalogue applies.
 */

// Swedish display labels. Presentation only — the role KEY is the contract.
const ROLE_LABELS = {
    accounts_payable: 'Leverantörsskulder',
    accounts_receivable: 'Kundfordringar',
    bank_fees: 'Bankavgifter',
    board_fee: 'Styrelsearvode',
    card_acquirer_receivable: 'Fordran kortinlösare (t.ex. Zettle)',
    corporate_tax_debt: 'Skatteskuld (bolagsskatt)',
    employee_payable: 'Skuld till anställda (utlägg m.m.)',
    employee_tax: 'Personalskatt',
    employer_contributions_expense: 'Arbetsgivaravgifter (kostnad)',
    interest_expense_nondeductible: 'Ej avdragsgill räntekostnad',
    interest_income_bank: 'Ränteintäkt bank',
    interest_income_taxfree: 'Skattefri ränteintäkt',
    inventory_asset: 'Lager',
    inventory_change: 'Lagerförändring',
    primary_bank: 'Företagskonto (bank)',
    purchases_trading_goods_eu_25: 'Varuinköp EU 25 %',
    purchases_trading_goods_import_25: 'Varuinköp import 25 %',
    rounding: 'Öresutjämning',
    salary_expense: 'Lönekostnad',
    salary_expense_executive: 'Lönekostnad företagsledare',
    salary_expense_worker: 'Lönekostnad kollektivanställda',
    sales_domestic_25: 'Försäljning Sverige 25 %',
    sales_domestic_12: 'Försäljning Sverige 12 %',
    sales_domestic_6: 'Försäljning Sverige 6 %',
    sales_domestic_exempt: 'Momsfri försäljning Sverige',
    sales_goods_domestic_25: 'Varuförsäljning Sverige 25 %',
    sales_goods_domestic_12: 'Varuförsäljning Sverige 12 %',
    sales_goods_domestic_6: 'Varuförsäljning Sverige 6 %',
    sales_goods_domestic_exempt: 'Momsfri varuförsäljning Sverige',
    sales_goods_eu: 'Varuförsäljning EU',
    sales_goods_export: 'Varuexport utanför EU',
    sales_reverse_domestic: 'Försäljning omvänd betalningsskyldighet (Sverige)',
    sales_services_domestic_25: 'Tjänsteförsäljning Sverige 25 %',
    sales_services_domestic_12: 'Tjänsteförsäljning Sverige 12 %',
    sales_services_domestic_6: 'Tjänsteförsäljning Sverige 6 %',
    sales_services_eu: 'Tjänsteförsäljning EU',
    sales_services_export: 'Tjänsteexport utanför EU',
    sales_unclassified_domestic: 'Oklassificerad försäljning Sverige',
    social_fees: 'Sociala avgifter (skuld)',
    tax_account: 'Skattekonto',
    vat_incoming: 'Ingående moms',
    vat_incoming_domestic: 'Ingående moms (Sverige)',
    vat_incoming_foreign: 'Ingående moms (utland)',
    vat_incoming_reverse_domestic: 'Ingående moms omvänd betalningsskyldighet (Sverige)',
    vat_invoice_output_25: 'Utgående moms kundfaktura 25 %',
    vat_invoice_output_12: 'Utgående moms kundfaktura 12 %',
    vat_invoice_output_6: 'Utgående moms kundfaktura 6 %',
    vat_outgoing_domestic: 'Utgående moms (Sverige)',
    vat_outgoing_domestic_25: 'Utgående moms Sverige 25 %',
    vat_outgoing_domestic_12: 'Utgående moms Sverige 12 %',
    vat_outgoing_domestic_6: 'Utgående moms Sverige 6 %',
    vat_outgoing_import_25: 'Utgående moms import 25 %',
    vat_outgoing_import_12: 'Utgående moms import 12 %',
    vat_outgoing_import_6: 'Utgående moms import 6 %',
    vat_outgoing_reverse_25: 'Utgående moms omvänd betalningsskyldighet 25 %',
    vat_outgoing_reverse_12: 'Utgående moms omvänd betalningsskyldighet 12 %',
    vat_outgoing_reverse_6: 'Utgående moms omvänd betalningsskyldighet 6 %',
    vat_outgoing_reverse_foreign_25: 'Utgående moms förvärv från utlandet 25 %',
    vat_outgoing_reverse_foreign_12: 'Utgående moms förvärv från utlandet 12 %',
    vat_outgoing_reverse_foreign_6: 'Utgående moms förvärv från utlandet 6 %',
    vat_receivable: 'Momsfordran',
    vat_settlement: 'Momsredovisning (avräkning)',
};

const FIXED_ASSET_LABELS = {
    machinery: 'maskiner', inventory: 'inventarier', installations: 'installationer',
    computers: 'datorer', vehicles: 'fordon', other: 'övriga anläggningstillgångar',
};

function roleLabel(role) {
    if (ROLE_LABELS[role]) return ROLE_LABELS[role];
    let match = role.match(/^fixed_asset_accdep_(\w+)$/);
    if (match) return `Ack. avskrivningar ${FIXED_ASSET_LABELS[match[1]] || match[1]}`;
    match = role.match(/^fixed_asset_depexp_(\w+)$/);
    if (match) return `Avskrivningskostnad ${FIXED_ASSET_LABELS[match[1]] || match[1]}`;
    match = role.match(/^fixed_asset_(\w+)$/);
    if (match) {
        if (match[1] === 'disposal_gain') return 'Vinst vid avyttring av anläggningstillgång';
        if (match[1] === 'disposal_loss') return 'Förlust vid avyttring av anläggningstillgång';
        if (match[1] === 'writedown_expense_inventory') return 'Nedskrivning inventarier';
        return `Anläggningstillgång ${FIXED_ASSET_LABELS[match[1]] || match[1]}`;
    }
    return role;
}

const GROUPS = [
    { key: 'core', title: 'Bank, betalning och reskontra', match: (r) => ['primary_bank', 'tax_account', 'accounts_payable', 'accounts_receivable', 'card_acquirer_receivable', 'bank_fees', 'rounding', 'employee_payable', 'corporate_tax_debt'].includes(r) },
    { key: 'sales', title: 'Försäljning', match: (r) => r.startsWith('sales_') },
    { key: 'vat', title: 'Moms', match: (r) => r.startsWith('vat_') },
    { key: 'payroll', title: 'Lön och personal', match: (r) => ['salary_expense', 'salary_expense_executive', 'salary_expense_worker', 'social_fees', 'employee_tax', 'employer_contributions_expense', 'board_fee'].includes(r) },
    { key: 'inventory', title: 'Lager och inköp', match: (r) => ['inventory_asset', 'inventory_change', 'purchases_trading_goods_eu_25', 'purchases_trading_goods_import_25'].includes(r) },
    { key: 'interest', title: 'Räntor', match: (r) => r.startsWith('interest_') },
    { key: 'assets', title: 'Anläggningstillgångar', match: (r) => r.startsWith('fixed_asset_') },
];

function groupRoles(roles) {
    const grouped = GROUPS.map((group) => ({ ...group, roles: [] }));
    const rest = { key: 'other', title: 'Övrigt', roles: [] };
    for (const entry of roles) {
        const group = grouped.find((candidate) => candidate.match(entry.role));
        (group ? group.roles : rest.roles).push(entry);
    }
    return [...grouped, rest].filter((group) => group.roles.length > 0);
}

function StatusBadge({ entry }) {
    if (entry.status === 'resolved' && entry.source === 'confirmed_override') {
        return <Badge bg="" style={{ background: 'var(--primary)' }}>Bekräftad</Badge>;
    }
    // Decided by a rule about the company's form rather than by a person — an
    // enskild firma's skattekonto goes through eget kapital, not 1630
    // (R-FORM-6). The badge says the company form decided it; the rule id stays
    // in the code and the register, where it means something.
    if (entry.status === 'resolved' && entry.source === 'form_rule_override') {
        return (
            <Badge bg="" style={{ background: 'var(--rgba-primary-8, #2f5e37)' }}>
                Företagsform
            </Badge>
        );
    }
    if (entry.status === 'resolved') {
        return <Badge bg="" style={{ background: 'var(--rgba-primary-8, #2f5e37)' }}>Standard</Badge>;
    }
    if (entry.status === 'unsupported') {
        return <Badge bg="secondary">Stöds ej på kontoplanen</Badge>;
    }
    if (entry.code === 'DEFAULT_ACCOUNT_NEEDS_MAPPING'
        || entry.code === 'DEFAULT_ACCOUNT_OFF_CHART'
        || entry.code === 'DEFAULT_ACCOUNT_ARCHIVED'
        || entry.code === 'CONFIRMED_OVERRIDE_ARCHIVED'
        || entry.code === 'CONFIRMED_OVERRIDE_OFF_CHART') {
        return <Badge bg="warning" text="dark">Saknar konto</Badge>;
    }
    return <Badge bg="danger">Fel</Badge>;
}

function needsAttention(entry) {
    return entry.status === 'error';
}

export default function ChartRolesSection({ companyId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [editingRole, setEditingRole] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showAll, setShowAll] = useState(false);
    // G47 — what this chart and company form can actually book. Derived from
    // the same role resolutions the table below shows, but stated in the terms
    // a user thinks in ("can I invoice a customer in Germany?") rather than as
    // a list of role keys.
    const [features, setFeatures] = useState([]);

    const load = useCallback(async ({ force = true } = {}) => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchChartRoles(companyId, { force });
            setData(result);
        } catch (requestError) {
            const message = requestError.response?.data?.message
                || 'Kontoplansrollerna kunde inte hämtas.';
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => { load({ force: false }); }, [load]);

    useEffect(() => {
        let cancelled = false;
        axios.get(getVatApiUrl(`${companyId}/reporting/vat-features`), { withCredentials: true })
            .then((response) => {
                if (!cancelled && response.data?.success) setFeatures(response.data.features || []);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [companyId]);

    // Archived accounts are not legal targets; the default accounts endpoint
    // already excludes them from pickers (I6).
    const loadAccounts = useCallback(async () => {
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/accounting/accounts`),
                { withCredentials: true }
            );
            setAccounts(response.data.accounts || []);
        } catch (requestError) {
            setAccounts([]);
        }
    }, [companyId]);

    const accountOptions = useMemo(() => accounts.map((account) => ({
        value: account.account_number,
        label: `${account.account_number} — ${account.account_name || ''}`,
    })), [accounts]);

    const beginEdit = (entry) => {
        setEditingRole(entry.role);
        setSelectedOption(null);
        if (accounts.length === 0) loadAccounts();
    };

    const confirm = async (entry) => {
        if (!selectedOption) return;
        setSaving(true);
        try {
            await confirmChartRole(companyId, entry.role, selectedOption.value);
            toast.success(`${roleLabel(entry.role)} pekar nu på ${selectedOption.value}.`);
            setEditingRole(null);
            setSelectedOption(null);
            await load();
        } catch (requestError) {
            const payload = requestError.response?.data;
            toast.error(payload?.message || 'Rollbekräftelsen kunde inte sparas.');
        } finally {
            setSaving(false);
        }
    };

    const attentionCount = (data?.roles || []).filter(needsAttention).length;
    const visibleRoles = (data?.roles || []).filter((entry) => (
        showAll || needsAttention(entry) || entry.status === 'resolved'
    ));
    const grouped = groupRoles(visibleRoles);

    return (
        <div className="modern-card" id="kontoplansroller" style={{ marginTop: '1.5rem' }}>
            <div className="modern-card-header">
                <div>
                    <h4 className="section-title">Kontoplansroller</h4>
                    <p className="section-subtitle">
                        Standardkonton som bokföringen använder
                        {data?.chartVersion ? ` — kontoplan ${data.chartVersion}` : ''}.
                        Peka om en roll för att använda ett annat konto, t.ex. ett eget
                        eller importerat konto.
                    </p>
                </div>
                <div className="action-buttons">
                    <Button variant="outline-secondary" size="sm" onClick={() => load()} disabled={loading}>
                        <RefreshCw size={14} className={loading ? 'spin' : ''} /> Uppdatera
                    </Button>
                </div>
            </div>
            <div className="modern-card-body">
                {error && (
                    <div style={{ color: 'var(--bs-danger, #dc3545)', marginBottom: '0.75rem' }}>
                        {error}
                    </div>
                )}

                {/* Silent when everything works. A panel that always says
                    "all good" trains people to stop reading it. */}
                {features.some((feature) => feature.status !== 'supported' && feature.status !== 'not_applicable') && (
                    <div
                        style={{
                            border: '1px solid var(--rgba-primary-1, #d7e3d9)',
                            borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem',
                        }}
                    >
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <AlertTriangle size={16} />
                            <strong>Momshantering som kontoplanen inte klarar</strong>
                        </div>
                        <p className="section-subtitle mb-2">
                            En faktura med den här momshanteringen kan inte bokföras, så den avvisas
                            när den skapas i stället för att bokföras fel.
                        </p>
                        <ul className="mb-0" style={{ paddingLeft: '1.1rem' }}>
                            {features
                                .filter((feature) => feature.status !== 'supported' && feature.status !== 'not_applicable')
                                .map((feature) => (
                                    <li key={feature.key} style={{ marginBottom: '0.35rem' }}>
                                        <strong>{feature.label}</strong>
                                        {feature.boxes?.length > 0 && (
                                            <span style={{ color: 'var(--secondary, #717579)' }}>
                                                {' '}(fält {feature.boxes.join(', ')})
                                            </span>
                                        )}
                                        <div style={{ fontSize: '0.8rem', color: 'var(--secondary, #717579)' }}>
                                            {feature.reason}
                                        </div>
                                    </li>
                                ))}
                        </ul>
                    </div>
                )}
                {loading && !data && (
                    <div style={{ padding: '1rem 0' }}><Spinner animation="border" size="sm" /> Hämtar roller…</div>
                )}
                {attentionCount > 0 && (
                    <div
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            padding: '0.6rem 0.9rem', borderRadius: 8, marginBottom: '1rem',
                            background: 'rgba(255,193,7,0.12)', border: '1px solid rgba(255,193,7,0.5)',
                        }}
                    >
                        <AlertTriangle size={16} />
                        <span>
                            {attentionCount === 1
                                ? '1 roll saknar konto — flöden som använder den fungerar inte förrän ett konto är valt.'
                                : `${attentionCount} roller saknar konto — flöden som använder dem fungerar inte förrän ett konto är valt.`}
                            {' '}Det händer t.ex. när kontoplansbytet bevarat ett äldre konto på
                            rollens standardnummer — välj kontot uttryckligen så används det igen.
                        </span>
                    </div>
                )}
                {data && (
                    <>
                        {grouped.map((group) => (
                            <div key={group.key} style={{ marginBottom: '1.1rem' }}>
                                <h6 style={{ color: 'var(--secondary, #717579)', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.04em' }}>
                                    {group.title}
                                </h6>
                                <div className="modern-table-container">
                                    <table className="modern-table" style={{ width: '100%' }}>
                                        <tbody>
                                            {group.roles.map((entry) => (
                                                <tr key={entry.role} style={needsAttention(entry) ? { background: 'rgba(255,193,7,0.07)' } : undefined}>
                                                    <td style={{ width: '38%' }}>
                                                        {roleLabel(entry.role)}
                                                        <div style={{ fontSize: '0.72rem', color: 'var(--secondary, #717579)' }}>{entry.role}</div>
                                                    </td>
                                                    <td style={{ width: '22%' }}>
                                                        {entry.status === 'resolved' ? (
                                                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                                <strong>{entry.accountNumber}</strong>
                                                                {entry.accountName ? ` — ${entry.accountName}` : ''}
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: 'var(--secondary, #717579)' }}>
                                                                {entry.defaultAccountNumber ? `standard ${entry.defaultAccountNumber}` : '—'}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ width: '20%' }}><StatusBadge entry={entry} /></td>
                                                    <td style={{ width: '20%', textAlign: 'right' }}>
                                                        {editingRole === entry.role ? (
                                                            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                                <div style={{ minWidth: 240, textAlign: 'left' }}>
                                                                    <Select
                                                                        options={accountOptions}
                                                                        value={selectedOption}
                                                                        onChange={setSelectedOption}
                                                                        theme={brandSelectTheme}
                                                                        placeholder="Välj konto…"
                                                                        isSearchable
                                                                        menuPlacement="auto"
                                                                    />
                                                                </div>
                                                                <Button size="sm" onClick={() => confirm(entry)} disabled={!selectedOption || saving}>
                                                                    {saving ? '…' : 'Bekräfta'}
                                                                </Button>
                                                                <Button size="sm" variant="outline-secondary" onClick={() => setEditingRole(null)}>
                                                                    Avbryt
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <Button size="sm" variant={needsAttention(entry) ? 'warning' : 'outline-secondary'} onClick={() => beginEdit(entry)}>
                                                                {needsAttention(entry) ? 'Välj konto' : 'Ändra'}
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Button variant="link" size="sm" style={{ paddingLeft: 0 }} onClick={() => setShowAll((value) => !value)}>
                                {showAll ? 'Dölj roller som saknar stöd på kontoplanen' : 'Visa även roller som saknar stöd på kontoplanen'}
                            </Button>
                            {attentionCount === 0 && data.roles?.length > 0 && (
                                <span style={{ color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                                    <CheckCircle2 size={14} /> Alla roller har ett konto.
                                </span>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
